import { getHighestMessageSeverity } from 'Components/Core/Message/helpers';
import { withInterfacing } from 'Components/Equipment/Interfacing';
import { getHighestSeverity } from 'Data/ViewModels/Core/Severity';
import { hasNonEmptyValue } from 'Lib/Any';
import className from 'classnames';
import { pick, uniqBy } from 'ramda';
import React, { useState } from 'react';
import { ConfirmDialog, MessageLinkTarget } from '..';
import { withErrorBoundary } from '../ErrorBoundary';
import { FieldLabel } from '../FieldLabel';
import { withInjectedProps } from '../InjectedPropsContext';
import { ComponentWithOwnLabel } from '../types';
import './Field.scss';
import { IFieldProps } from './types';

let idCounter = 0;

/**
 * This provides a wrapper for standard UI inputs and outputs.
 *
 * Usage: <Field input={<ComponentName />} {...inputProps} />
 */
const FieldBase: React.FC<React.PropsWithChildren<IFieldProps>> = (props) => {
  const [internalId] = useState(`field-${idCounter++}`);

  const {
    loading,
    children,
    id: fixedId,
    input,
    messages,
    limit,
    visible = true,
    severity: fixedSeverity,
    additionalMessagePaths,
    additionalInformationDialogContent,
    additionalInformationDialogTitle,
    additionalInformationDialogVisible,
    fieldAdditionalClassName,
    horizontal,
    horizontalLabelWidth,
    ...propsForChildren
  } = props;

  const { label, inGrid = false } = propsForChildren as any;
  const id = fixedId ?? internalId;

  const messageSeverity = getHighestMessageSeverity(messages);
  const limitSeverity = limit && !limit.IsPassing ? 'error' : undefined;
  const severity = getHighestSeverity([fixedSeverity, messageSeverity, limitSeverity]);
  /**
   * Determine a different severity for input highlighting as we currently highlight inputs differently
   * based on whether they are in a grid.
   *
   * TODO: Fix up input theming so we don't have to do this.
   */
  const inputSeverity = inGrid ? severity : getHighestSeverity([fixedSeverity, limitSeverity]);

  const finalPropsForChildren = {
    ...propsForChildren,
    severity: inputSeverity,
    id,
  };

  /*
   * Guard clauses
   */
  if (children) throw new Error(`Children are not allowed in Field, use input prop <Field input={<Component>}/>`);
  if (!input) throw new Error(`Field requires child definition via input prop <Field input={<Component>}/>`);
  if (input.type === React.Fragment) throw new Error(`Field input prop should not be a React Fragment, but an input.`);

  /*
   * Create input element
   */
  let finalInput: React.ReactNode = undefined;
  // If input type is a React component, wrap it using withInterfacing HOC so interfacing features are available
  finalInput = hasNonEmptyValue(input.type as React.ComponentType)
    ? withInterfacing(input.type as React.ComponentType)({ ...finalPropsForChildren, children: undefined })
    : React.createElement(input.type, finalPropsForChildren);
  if (ConfirmDialog.hasConfirmProps(props)) {
    finalInput = <ConfirmDialog {...props}>{finalInput}</ConfirmDialog>;
  }

  if (!visible) return null;

  /*
   * Create label element
   */
  const managesOwnLabel = (input.type as ComponentWithOwnLabel<any, any>).managesOwnLabel;
  let finalLabel: React.ReactNode = undefined;

  if (!managesOwnLabel && !inGrid) {
    if ((input.type as ComponentWithOwnLabel<any, any>).Label) {
      finalLabel = (
        <div
          className={className('Field-Label', {
            [`Field-Label-horizontal`]: horizontal,
            [`Field-Label-medium`]: horizontalLabelWidth === 'medium',
            [`Field-Label-long`]: horizontalLabelWidth === 'long',
          })}
        >
          {React.createElement((input.type as any).Label, finalPropsForChildren as any)}
        </div>
      );
    } else if (label) {
      finalLabel = (
        <FieldLabel
          className={className('Field-Label', {
            [`Field-Label-horizontal`]: horizontal,
            [`Field-Label-medium`]: horizontalLabelWidth === 'medium',
            [`Field-Label-long`]: horizontalLabelWidth === 'long',
          })}
          htmlFor={id}
          additionalInformationDialogContent={additionalInformationDialogContent}
          additionalInformationDialogTitle={additionalInformationDialogTitle}
          additionalInformationDialogVisible={additionalInformationDialogVisible}
          {...pick(['noRecase', 'emph'], finalPropsForChildren)}
        >
          {label}
        </FieldLabel>
      );
    }
  }

  return (
    <div className={className('Field', { [`Field-horizontal`]: horizontal }, fieldAdditionalClassName)}>
      {/* Input Label */}
      {finalLabel}
      {/* Input Control */}
      <span
        className={className('Field-Input', {
          [`Field-Input-${severity}`]: severity,
          [`Field-Input-inGrid`]: inGrid,
          [`Field-Input-horizontal`]: horizontal,
        })}
      >
        {messages &&
          messages.map(
            ({ linkToId, id, error, severity }, i) =>
              !linkToId && id && (error || severity === 'error') && <MessageLinkTarget key={i} id={id} />
          )}
        {loading ? <div className="Field-Input-loading" /> : finalInput}
      </span>
      {/* Messages */}
      {messages &&
        !inGrid &&
        !loading &&
        uniqBy((m) => m.message, messages).map(({ message, severity }, i) => (
          <div
            key={i}
            className={className('Field-Message', {
              [`Field-Message-${severity}`]: severity,
              [`Field-Message-horizontal`]: horizontal,
            })}
          >
            {message}
          </div>
        ))}
    </div>
  );
};

const Field = withErrorBoundary(withInjectedProps(FieldBase));

export { Field, FieldBase };
export default Field;
